import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import {
  SITE_CONTACT_EMAIL,
  SITE_GRIEVANCE_OFFICER_NAME,
  SITE_LEGAL_LAST_UPDATED,
  SITE_NAME,
} from '../config/site';

export default function PrivacyPolicyPage() {
  const title = `Privacy Policy | ${SITE_NAME}`;
  const description = `How ${SITE_NAME} collects, uses, and protects personal data under India's Digital Personal Data Protection Act, 2023 (DPDP).`;

  return (
    <>
      <SeoMeta title={title} description={description} path="/privacy-policy" ogType="website" />

      <main className="static-page legal-page">
        <header className="container static-page-hero">
          <h1>Privacy Policy</h1>
          <p className="static-page-lead legal-meta">Last updated: {SITE_LEGAL_LAST_UPDATED}.</p>
        </header>

        <article className="container static-page-body legal-body">
          <section aria-label="Introduction">
            <p>
              {SITE_NAME} (&ldquo;we&rdquo;, &ldquo;our&rdquo;, &ldquo;us&rdquo;) respects your privacy and is committed to
              protecting your personal data in accordance with the Digital Personal Data Protection Act, 2023 (India).
            </p>
          </section>

          <section aria-labelledby="collect-heading">
            <h2 id="collect-heading">1. Data We Collect</h2>
            <p>We may collect:</p>
            <h3>a) Personal data</h3>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Age, height, weight (if you provide them)</li>
              <li>Preferences (e.g., diet type, goals)</li>
            </ul>
            <h3>b) Usage data</h3>
            <ul>
              <li>App or website activity</li>
              <li>Feature interactions</li>
              <li>Device and browser information</li>
            </ul>
            <h3>c) Optional data</h3>
            <ul>
              <li>Meal selections</li>
              <li>Progress inputs</li>
            </ul>
            <p>We do <strong>not</strong> collect sensitive health data without explicit consent where such consent is required by law.</p>
          </section>

          <section aria-labelledby="purpose-heading">
            <h2 id="purpose-heading">2. Purpose of Data Use</h2>
            <p>We use your data to:</p>
            <ul>
              <li>Provide personalised plans and guidance</li>
              <li>Improve our services</li>
              <li>Communicate updates and offers</li>
              <li>Analyse usage patterns in aggregate or pseudonymous form where appropriate</li>
            </ul>
          </section>

          <section aria-labelledby="consent-heading">
            <h2 id="consent-heading">3. Consent</h2>
            <p>
              By using our Services, you consent to collection and processing as described in this Policy, except where another
              lawful basis applies. You may withdraw consent at any time by contacting us; withdrawal may limit certain features.
            </p>
          </section>

          <section aria-labelledby="sharing-heading">
            <h2 id="sharing-heading">4. Data Sharing</h2>
            <p>We do <strong>not</strong> sell your personal data.</p>
            <p>We may share data with:</p>
            <ul>
              <li>Service providers (e.g., hosting, analytics) under appropriate safeguards</li>
              <li>Legal authorities when required by law</li>
            </ul>
          </section>

          <section aria-labelledby="storage-heading">
            <h2 id="storage-heading">5. Data Storage</h2>
            <p>
              Your data may be stored on secure servers located in India or in other jurisdictions that provide adequate
              protection, consistent with applicable law and our agreements with processors.
            </p>
          </section>

          <section aria-labelledby="retention-heading">
            <h2 id="retention-heading">6. Data Retention</h2>
            <p>We retain data only as long as necessary for:</p>
            <ul>
              <li>Service delivery</li>
              <li>Legal and regulatory compliance</li>
            </ul>
          </section>

          <section aria-labelledby="rights-heading">
            <h2 id="rights-heading">7. Your Rights (DPDP Act)</h2>
            <p>Subject to applicable law, you have the right to:</p>
            <ul>
              <li>Access your data</li>
              <li>Correct inaccuracies</li>
              <li>Request deletion where applicable</li>
              <li>Withdraw consent where processing is consent-based</li>
              <li>Grievance redressal as described below</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href={`mailto:${SITE_CONTACT_EMAIL}`}>{SITE_CONTACT_EMAIL}</a> or use our{' '}
              <Link to="/contact">contact form</Link>.
            </p>
          </section>

          <section aria-labelledby="security-heading">
            <h2 id="security-heading">8. Security</h2>
            <p>We implement reasonable security safeguards, including encryption, access control, and secure storage where appropriate. No system is 100% secure.</p>
          </section>

          <section aria-labelledby="cookies-heading">
            <h2 id="cookies-heading">9. Cookies &amp; Tracking</h2>
            <p>We may use cookies and similar technologies to improve the user experience and analyse traffic. You can disable cookies via your browser settings; some features may not work as intended.</p>
          </section>

          <section aria-labelledby="children-heading">
            <h2 id="children-heading">10. Children&rsquo;s Privacy</h2>
            <p>We do not knowingly collect data from users under 18 without appropriate parental consent where required by law.</p>
          </section>

          <section aria-labelledby="third-party-heading">
            <h2 id="third-party-heading">11. Third-Party Services</h2>
            <p>
              We may use analytics tools, payment gateways, and other processors. Those services have their own privacy policies;
              we encourage you to read them.
            </p>
          </section>

          <section aria-labelledby="updates-heading">
            <h2 id="updates-heading">12. Updates to This Policy</h2>
            <p>We may update this Privacy Policy. Changes will be posted on this page with an updated &ldquo;Last updated&rdquo; date.</p>
          </section>

          <section aria-labelledby="grievance-heading">
            <h2 id="grievance-heading">13. Grievance Officer (India)</h2>
            <p>
              <strong>Name:</strong> {SITE_GRIEVANCE_OFFICER_NAME}
              <br />
              <strong>Email:</strong>{' '}
              <a href={`mailto:${SITE_CONTACT_EMAIL}`}>{SITE_CONTACT_EMAIL}</a>
            </p>
          </section>

          <section aria-labelledby="contact-heading">
            <h2 id="contact-heading">Contact</h2>
            <p>
              <a href={`mailto:${SITE_CONTACT_EMAIL}`}>{SITE_CONTACT_EMAIL}</a>
              {' · '}
              <Link to="/contact">Contact form</Link>
            </p>
          </section>

          <footer className="static-page-foot">
            <p>
              <Link to="/terms-of-service" rel="nofollow">
                Terms of Use
              </Link>
              {' · '}
              <Link to="/">Home</Link>
            </p>
          </footer>
        </article>
      </main>
    </>
  );
}
